import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ElasticFormModal } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { globalActions, statusBoxActions } from 'primary-components';

import { htmlEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import FilePickerModal from 'primary/pickers/FilePicker/Modal';

import { getToolbar, htmlConfig, plugins, usePngSelectorAction } from './HtmlEditorConst';

import { useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import { When } from 'react-if';

import './HtmlEditor.scss';

const HtmlEditor = () => {
  const dispatch = useDispatch(),
        [defaultContent, setDefaultContent] = useState(),
        [content, setContent] = useState(),
        [fileInfo, setFileInfo] = useState({}),
        [pngSelectorIsOpen, setPngSelectorIsOpen] = useState(false),
        [submitPngSelector, setSubmitPngSelector] = useState(() => () => {}),
        { fileId, disabled, provider } = useSelector(state => state.toolsState.htmlEditorState),
        closePngSelector = useCallback(() => setPngSelectorIsOpen(false), []),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.headers?.get("Location"), [infoResponse]),
        { mutateAsync: s3GetCall, isPending } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3, url: location }),
        downloadHtml = useCallback(() => {
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return s3GetCall()
                .then(response => response.text())
                .then(response => {
                  const formattedResponse = response.replace(/\n"/g, '"')
                  setDefaultContent(formattedResponse)
                })
                .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [dispatch, s3GetCall]),
        close = useCallback(() => {
          dispatch(htmlEditorActions.changeValue(undefined, "fileId"))
          dispatch(htmlEditorActions.changeValue(false, "open"))
          dispatch(htmlEditorActions.changeValue(false, "disabled"))
          dispatch(htmlEditorActions.changeValue(undefined, "provider"))
        }, [dispatch]),
        onChange = useCallback(content => setContent(content), [setContent]),
        { mutateAsync: getFileLocationCall } = filesDatalakeQueries.useGetFileDefinitionMutation({ getQueryBuilder }),
        // eslint-disable-next-line
        beforeMount = usePngSelectorAction(getFileLocationCall, s3GetCall, setPngSelectorIsOpen, setSubmitPngSelector, tinymce),
        queryClient = useQueryClient(),
        fileFields = useMemo(() => Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v })), [fileInfo]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        invalidateRef = useRef(),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileName: fileInfo?.fileName, groupName: fileInfo?.groupName, fileType: fileInfo?.fileType, fields: fileFields }),
        onSubmit = useCallback(() => {
          if(isPending) {
            return Promise.resolve();
          }
          if(disabled) {
            return Promise.resolve();
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata()
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
              image: content,
              url: response.location
            }))
            .then(() => dispatch(globalActions.getMessageStore()).success("New version of file has been saved."))
            .then(() => {
              close()
              clearTimeout(invalidateRef.current)
              invalidateRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [isPending, disabled, dispatch, uploadFileMetadata, fileInfo.fileName, fileInfo.groupName, uploadFileContent, content, close, queryClient]),
        submitProps = useMemo(() => ({
          disabled
        }), [disabled])

  useEffect(() => {
    if(location) {
      downloadHtml()
    }
  }, [downloadHtml, location])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "html_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => {
    if(!infoResponse?.bodyUsed) {
      infoResponse?.json().then(r => setFileInfo(r || {}))
    }
  }, [infoResponse])

  useEffect(() => () => {
    clearTimeout(invalidateRef.current)
  }, [])

  return (
    <Fragment>
      <ElasticFormModal
          className="ng-html-editor"
          closeOnEsc={false}
          closeOnSubmit={false}
          onClose={close}
          onSubmit={onSubmit}
          open={defaultContent !== undefined}
          size="large"// we still override this in scss
          sizeWidth="large" // we still override this scss
          submitProps={submitProps}
          title="Html Editor"
      >
        <Editor
            init={htmlConfig}
            initialValue={defaultContent}
            licenseKey="gpl"
            onEditorChange={onChange}
            plugins={plugins}
            toolbar={getToolbar()}
        />
      </ElasticFormModal>
      <When condition={pngSelectorIsOpen}>
        {() => (
          <FilePickerModal
              closeModal={closePngSelector}
              onSubmit={submitPngSelector}
          />
        )}
      </When>
    </Fragment>
  )
}

export default memo(HtmlEditor);